import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronRight, Eye, EyeOff, Gift, Globe, Headphones, Users, Zap } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { openExternal } from '@/lib/telegram';
import { useI18n, LANGS, type Lang } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { CopyRow, Notice, PageLoading, PageTitle } from '@/components/ui';
import { PixProfileForm } from '@/components/PixProfileForm';
import { NeedAccount } from './NeedAccount';
import type { PixProfile } from '@/types/api';

function Credentials() {
    const { t, te } = useI18n();
    const [show, setShow] = useState(false);
    const q = useQuery({ queryKey: ['account'], queryFn: async () => (await api.get<{ username: string; password: string | null; playerId: string; siteUrl: string }>('/account')).data });
    if (q.isLoading) return <PageLoading />;
    if (q.isError || !q.data) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    const a = q.data;
    return (
        <section className="space-y-2">
            <h3 className="section-title">{t('account.title')}</h3>
            <CopyRow label={t('account.username')} value={a.username} />
            {a.password && (
                <CopyRow label={t('account.password')} value={a.password} display={show ? a.password : '••••••••••'}
                         action={<button className="flex items-center gap-1 text-brand-300" onClick={() => setShow(s => !s)}>{show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {show ? t('account.hidePassword') : t('account.showPassword')}</button>} />
            )}
            <CopyRow label={t('account.playerId')} value={a.playerId} />
            {a.siteUrl && <button className="btn-primary w-full" onClick={() => openExternal(a.siteUrl)}><Globe className="w-4 h-4" /> {t('account.openSite')}</button>}
        </section>
    );
}

function PixDetails() {
    const { t, te } = useI18n();
    const [editing, setEditing] = useState(false);
    const q = useQuery({ queryKey: ['pix', 'profile'], queryFn: async () => (await api.get<PixProfile>('/pix/profile')).data });
    if (q.isLoading || !q.data) return q.isError ? <Notice tone="warn">{te(apiError(q.error))}</Notice> : null;
    const p = q.data;
    const fields = Array.from(new Set(['name', 'cpf', ...p.asked]));
    return (
        <section className="space-y-2">
            <h3 className="section-title flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-accent-300" /> {t('pix.title')}</h3>
            {editing ? (
                <PixProfileForm profile={p} fields={fields} onSaved={() => setEditing(false)} />
            ) : (
                <div className="card py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0 text-sm">
                        {p.profile.name ? (
                            <>
                                <div className="font-semibold truncate">{p.profile.name}</div>
                                <div className="text-[11px] text-ink-muted font-mono">CPF {p.profile.cpf}</div>
                            </>
                        ) : <span className="text-ink-muted">{t('pix.notSet')}</span>}
                    </div>
                    <button className="btn-secondary px-3 py-2 text-xs" onClick={() => setEditing(true)}>{p.profile.name ? t('common.edit') : t('common.add')}</button>
                </div>
            )}
        </section>
    );
}

export function ProfilePage() {
    const { t, lang, setLang } = useI18n();
    const { me } = useAuth();
    const navigate = useNavigate();
    if (!me?.registered) return <NeedAccount />;
    const links = [
        { icon: Users, label: t('home.tiles.referrals'), to: '/referrals' },
        { icon: Gift, label: t('home.tiles.giftCodes'), to: '/gift-codes' },
        { icon: Headphones, label: t('home.tiles.support'), to: '/support' }
    ];
    return (
        <div className="space-y-5">
            <PageTitle title={t('account.pageTitle')} subtitle={me.user.username ? `@${me.user.username}` : me.user.firstName} />
            <Credentials />
            <PixDetails />
            <section className="space-y-2">
                <h3 className="section-title">{t('account.language')}</h3>
                <div className="grid grid-cols-2 gap-2">
                    {LANGS.map((l: Lang) => (
                        <button key={l} onClick={() => { setLang(l); void api.post('/lang', { lang: l }).catch(() => undefined); }}
                            className={clsx('rounded-xl border px-3 py-3 text-sm font-semibold transition', lang === l ? 'border-brand-400/60 bg-brand-400/10' : 'border-white/10 bg-white/[.03]')}>
                            {l === 'pt' ? '🇧🇷 Português' : '🇬🇧 English'}
                        </button>
                    ))}
                </div>
            </section>
            <section className="space-y-2">
                {links.map(l => (
                    <button key={l.to} className="card card-tap w-full flex items-center gap-3 py-3" onClick={() => navigate(l.to)}>
                        <div className="p-2 rounded-lg bg-brand-400/10 text-brand-300 border border-brand-400/20"><l.icon className="w-4 h-4" /></div>
                        <span className="flex-1 text-left text-sm font-medium">{l.label}</span>
                        <ChevronRight className="w-4 h-4 text-ink-muted" />
                    </button>
                ))}
            </section>
        </div>
    );
}
